import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useSpring, useInView, AnimatePresence } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { 
  ArrowRight, Check, Star,
  Sparkles, Scissors, Droplets, Dumbbell,
  Eye, Palette
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ShopifyBuyButton from '../components/ShopifyBuyButton';

// Countdown timer hook
const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = new Date(targetDate) - new Date();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };
    
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);
  
  return timeLeft;
};

// Apple-style smooth spring config
const smoothSpring = {
  type: "spring",
  stiffness: 100,
  damping: 30,
  mass: 1
};

// Premium ease curve (Apple-like)
const premiumEase = [0.25, 0.46, 0.45, 0.94];

// Animation variants with smoother timing
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 1, ease: premiumEase }
  }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 1, ease: premiumEase }
  }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 1, ease: premiumEase }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { duration: 0.8, ease: premiumEase }
  }
};

// Smooth card animation for mobile
const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: { duration: 0.7, ease: premiumEase }
  }
};

const WHAT_YOU_GET = [
  { icon: Eye, title: "Contrast in face", desc: "Sharper features, defined jawline, enhanced facial structure" },
  { icon: Sparkles, title: "Psychology", desc: "Confidence, presence, how to carry yourself" },
  { icon: Dumbbell, title: "Lean fit body", desc: "Get defined, aesthetic physique that stands out" },
  { icon: Droplets, title: "Skin & complexion", desc: "Clear skin, better texture, healthy glow" },
  { icon: Scissors, title: "Hair & styling", desc: "Cuts and styles that suit your face shape" },
  { icon: Palette, title: "Coloring & contrast", desc: "Work with your natural tones for maximum impact" },
];

const PROBLEMS = [
  "Guess their haircut and hope it works",
  "Use random products with no real results",
  "Don't know how to enhance their features",
  "Waste months on trial and error",
];

// Interactive Stacked Card Component - Cards offset with click to reveal
const TransformationCard = ({ before, after, label, isMain = false }) => {
  const [showAfter, setShowAfter] = useState(false);
  
  // Random-ish offsets for each card to make them look natural
  const beforeRotation = isMain ? -3 : -5;
  const beforeOffset = isMain ? { x: -8, y: -6 } : { x: -6, y: -4 };
  
  const handleClick = () => {
    if (!showAfter) {
      setShowAfter(true);
    }
  };
  
  return (
    <div 
      className={`relative cursor-pointer ${isMain ? 'h-[400px] md:h-[500px] max-w-sm mx-auto' : 'h-[280px] md:h-[320px]'}`}
      onClick={handleClick}
    >
      {/* After Card (Bottom) */}
      <div 
        className={`absolute inset-4 md:inset-6 transition-all duration-500 ${showAfter ? 'scale-105' : 'scale-100'}`}
        style={{ 
          transform: `rotate(2deg)`,
          zIndex: 1
        }}
      >
        <div className={`relative w-full h-full border-2 ${showAfter ? 'border-[#D4AF37]' : 'border-[#333]'} bg-black overflow-hidden shadow-2xl`}>
          <img 
            src={after}
            alt="After"
            className={`w-full h-full object-cover ${isMain ? 'object-center' : 'object-top'}`}
          />
          <div className={`absolute top-2 left-2 px-2 py-1 text-[10px] md:text-xs font-semibold tracking-wide bg-[#D4AF37] text-black`}>
            AFTER
          </div>
          {label && (
            <div className="absolute bottom-2 left-2 right-2 text-center">
              <span className="text-[#D4AF37] text-xs font-medium bg-black/50 px-2 py-1">{label}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Before Card (Top - slides away on click, no return) */}
      <div 
        className={`absolute inset-4 md:inset-6 transition-all duration-500 ease-out ${showAfter ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        style={{ 
          transform: showAfter 
            ? `rotate(${beforeRotation - 15}deg) translate(-120%, -20%)` 
            : `rotate(${beforeRotation}deg) translate(${beforeOffset.x}px, ${beforeOffset.y}px)`,
          zIndex: 2
        }}
      >
        <div className="relative w-full h-full border-2 border-[#555] bg-black overflow-hidden shadow-2xl">
          <img 
            src={before}
            alt="Before"
            className="w-full h-full object-cover object-top grayscale"
          />
          <div className="absolute top-2 left-2 px-2 py-1 text-[10px] md:text-xs font-semibold tracking-wide bg-black/80 text-[#888]">
            BEFORE
          </div>
        </div>
      </div>
      
      {/* Tap hint */}
      {!showAfter && (
        <div className="absolute bottom-0 left-0 right-0 text-center">
          <span className="text-[#555] text-[10px]">tap to reveal</span>
        </div>
      )}
    </div>
  );
};

// Simple stack of 6 photos - tap to dismiss
const ClientStack = () => {
  const [dismissed, setDismissed] = useState(0);
  
  // All 6 cards in exact order you want to show them
  const allCards = [
    { src: "/assets/transformation/before1_new.jpg", label: 'BEFORE' },
    { src: "/assets/transformation/after1_new.jpg", label: 'AFTER' },
    { src: "/assets/transformation/before3_new.jpg", label: 'BEFORE' },
    { src: "/assets/transformation/after3_video.mp4", label: 'AFTER', isVideo: true },
    { src: "/assets/transformation/before2_new.jpg", label: 'BEFORE' },
    { src: "/assets/transformation/after2_new.jpg", label: 'AFTER' },
  ];
  
  return (
    <div 
      className="relative max-w-sm mx-auto cursor-pointer"
      style={{ height: '450px' }}
      onClick={() => dismissed < allCards.length && setDismissed(dismissed + 1)}
    >
      {allCards.map((card, idx) => {
        const stackPos = idx - dismissed;
        const isGone = idx < dismissed;
        const isAfter = card.label === 'AFTER';
        
        if (stackPos > 5) return null;
        
        return (
          <div
            key={idx}
            className="absolute transition-all duration-300 ease-out"
            style={{
              top: 0,
              left: 0,
              right: 0,
              zIndex: allCards.length - idx,
              opacity: isGone ? 0 : 1,
              transform: isGone 
                ? 'translateX(-120%) rotate(-20deg)' 
                : `translateX(${stackPos * 10}px) translateY(${stackPos * 8}px) rotate(${stackPos * 2}deg)`,
            }}
          >
            <div className={`aspect-[3/4] border-2 ${isAfter ? 'border-[#D4AF37]' : 'border-[#444]'} bg-black overflow-hidden shadow-xl`}>
              {card.isVideo ? (
                <video src={card.src} className="w-full h-full object-cover object-top" autoPlay loop muted playsInline />
              ) : (
                <img src={card.src} alt={card.label} className={`w-full h-full object-cover object-top ${!isAfter ? 'grayscale' : ''}`} />
              )}
              <div className={`absolute top-3 left-3 px-3 py-1 text-xs font-bold ${isAfter ? 'bg-[#D4AF37] text-black' : 'bg-black/70 text-[#888]'}`}>
                {card.label}
              </div>
            </div>
          </div>
        );
      })}
      
      <div className="absolute -bottom-6 left-0 right-0 text-center">
        <p className="text-[#555] text-xs">{dismissed < allCards.length ? 'tap to continue' : '✓'}</p>
      </div>
    </div>
  );
};

export default function Home() {
  const heroRef = useRef(null);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  // Smooth spring-based parallax
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });
  const heroOpacity = useTransform(smoothProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(smoothProgress, [0, 0.5], [1, 1.1]);
  const heroY = useTransform(smoothProgress, [0, 1], ["0%", "20%"]);

  // Scroll to top on mount (fixes video sizing issue on navigation)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Show sticky CTA after scrolling past hero
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > window.innerHeight * 0.8);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="overflow-hidden bg-black">

      {/* ============== HERO SECTION ============== */}
      <section 
        ref={heroRef}
        className="min-h-screen relative flex items-center overflow-hidden pt-16" 
        data-testid="hero-section"
      >
        {/* Background Image - clean, no video issues */}
        <motion.div 
          className="absolute inset-0 z-0 overflow-hidden bg-black"
          style={{ scale: heroScale, y: heroY }}
          initial={{ opacity: 1 }}
        >
          <img 
            src={`${process.env.REACT_APP_BACKEND_URL}/api/static/hero-poster.jpg`}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.35) contrast(0.95)' }}
          />
          {/* Dark gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70 pointer-events-none" />
        </motion.div>
        
        <motion.div 
          className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-32 w-full"
          style={{ opacity: heroOpacity }}
        >
          <div className="max-w-3xl mx-auto text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.3, ease: premiumEase }}
              className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-white mb-6 md:mb-8"
            >
              Not genetics. Not luck.<br/>Just this.
            </motion.h1>
            
            <motion.div 
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1, delay: 0.6, ease: premiumEase }}
              className="max-w-xs mx-auto"
            >
              <ShopifyBuyButton productId="signal-full-guide" variant="primary" />
              
              {/* Price only */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1, duration: 0.8 }}
                className="mt-4 text-center"
              >
                <div className="flex items-center justify-center gap-3 mb-1">
                  <span className="text-white text-2xl font-semibold">€19</span>
                </div>
                <p className="text-[#666] text-xs mb-2">
                  one-time · lifetime access
                </p>
                <p className="text-[#555555] text-[10px] mb-3 leading-relaxed">
                  By purchasing you agree to our{' '}
                  <Link to="/terms" className="text-[#D4AF37] hover:underline">terms</Link>
                  {' '}and waive your right of withdrawal.
                </p>
                <p className="text-[#555555] text-xs">
                  500+ men already upgrading
                </p>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
        
        {/* Scroll indicator - smooth bounce */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 1, ease: premiumEase }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 hidden md:block"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-6 h-10 border-2 border-[#333] rounded-full flex justify-center pt-2"
          >
            <motion.div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* ============== TRANSFORMATION - Interactive Cards ============== */}
      <section className="py-12 md:py-20 bg-black border-t border-[#1a1a1a]" data-testid="transformation-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.p 
              variants={fadeInUp} 
              className="text-[#666] text-xs uppercase tracking-widest mb-3 text-center"
            >
              The Transformation
            </motion.p>
            <motion.h2 
              variants={fadeInUp} 
              className="font-serif text-2xl md:text-3xl text-white mb-10 text-center"
            >
              Real results. Real people.
            </motion.h2>
            
            {/* Main Transformation - The Founder */}
            <motion.div variants={fadeInUp} className="mb-10">
              <TransformationCard 
                before="/assets/transformation/before4_new.jpg"
                after="/assets/transformation/after4_new.jpg"
                label="The Creator"
                isMain={true}
              />
            </motion.div>
            
            {/* Client Transformations - One big stack */}
            <motion.p 
              variants={fadeInUp} 
              className="text-[#666] text-xs uppercase tracking-widest mb-4 text-center"
            >
              People I've helped
            </motion.p>
            <motion.div variants={fadeInUp}>
              <ClientStack />
            </motion.div>
            
            <motion.p 
              variants={fadeInUp} 
              className="text-[#555] text-xs text-center mt-4"
            >
              Tap to see transformations
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ============== WHAT IS THE SIGNAL ============== */}
      <section className="py-16 md:py-24 bg-[#0a0a0a] border-t border-[#1a1a1a]" data-testid="what-is-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            {/* Title */}
            <motion.h2 
              variants={fadeInUp} 
              className="font-serif text-2xl sm:text-3xl md:text-4xl text-white mb-10 text-center"
            >
              What You Get
            </motion.h2>
            
            {/* Cover Image + Text Grid */}
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Cover Image */}
              <motion.div 
                variants={fadeInLeft}
                className="relative group"
              >
                <div className="relative overflow-hidden border border-[#1a1a1a] shadow-2xl">
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/guide/cover`}
                    alt="The Signal: Upgrade - Guide Preview"
                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                </div>
                {/* Badge */}
                <div className="absolute -bottom-3 -right-3 bg-[#D4AF37] text-black px-4 py-2 text-xs font-semibold uppercase tracking-wider">
                  58 Pages
                </div>
              </motion.div>
              
              {/* Text Content */}
              <motion.div variants={fadeInRight}>
                <p className="text-base md:text-lg text-[#999999] leading-relaxed mb-6">
                  A step-by-step system covering everything: skin, hair, face, body, and style. 
                  Follow it. Put in the work. See the results.
                </p>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-3 mb-8">
                  {['Complete System', 'Daily Routines', 'No Guesswork'].map((tag, idx) => (
                    <span 
                      key={idx}
                      className="text-white text-xs tracking-wide border border-[#333] px-4 py-2"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                
                {/* CTA */}
                <div className="max-w-xs">
                  <ShopifyBuyButton productId="signal-full-guide" variant="primary" />
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============== WHAT YOU GET ============== */}
      <section className="py-16 md:py-24 bg-black" data-testid="what-you-get-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp} 
              className="font-serif text-2xl sm:text-3xl md:text-4xl text-white mb-4 text-center"
            >
              Inside The Signal
            </motion.h2>
            <motion.p 
              variants={fadeInUp} 
              className="text-[#999999] text-center mb-10 max-w-lg mx-auto text-sm md:text-base"
            >
              Everything you need. Nothing you don't. Follow the system, put in the work.
            </motion.p>
            
            <motion.div 
              variants={staggerContainer}
              className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4"
            >
              {WHAT_YOU_GET.map((item, idx) => (
                <motion.div 
                  key={idx}
                  variants={cardVariants}
                  className="bg-[#0a0a0a] border border-[#1a1a1a] p-4 md:p-6 hover:border-[#D4AF37]/50 transition-colors duration-300"
                >
                  <item.icon className="w-5 h-5 text-[#D4AF37] mb-3" />
                  <h3 className="text-white font-medium text-sm">{item.title}</h3>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============== GUIDE PREVIEW ============== */}
      <section className="py-12 md:py-16 bg-[#0a0a0a] border-t border-[#1a1a1a]" data-testid="guide-preview-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            <motion.p 
              variants={fadeInUp} 
              className="text-[#666] text-xs uppercase tracking-widest mb-6 text-center"
            >
              Preview The Guide
            </motion.p>
            
            {/* Preview Grid - Pages 1, 3, 4 */}
            <motion.div 
              variants={staggerContainer}
              className="grid grid-cols-3 gap-4 max-w-3xl mx-auto"
            >
              {[
                { page: 1, label: 'Page 1' },
                { page: 3, label: 'Page 3' },
                { page: 4, label: 'Page 4' }
              ].map((item) => (
                <motion.div 
                  key={`preview-${item.page}`}
                  variants={fadeInUp}
                  className="relative group"
                >
                  <div className="overflow-hidden border border-[#1a1a1a] bg-[#0a0a0a] aspect-[3/4] flex items-center justify-center">
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/guide/preview/${item.page}`}
                      alt={item.label}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<span class="text-[#333] text-xs">Loading...</span>';
                      }}
                    />
                  </div>
                </motion.div>
              ))}
            </motion.div>
            
            {/* CTA */}
            <motion.div variants={fadeInUp} className="mt-8 text-center">
              <p className="text-[#666] text-xs mb-4">58 pages of actionable content</p>
              <div className="max-w-xs mx-auto">
                <ShopifyBuyButton productId="signal-full-guide" variant="primary" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============== THE DEAL ============== */}
      <section className="py-16 md:py-24 bg-black border-t border-[#1a1a1a]" data-testid="problem-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center"
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-serif text-2xl md:text-4xl text-white mb-6"
            >
              This isn't magic. It's a system.
            </motion.h2>
            
            <motion.p 
              variants={fadeInUp}
              className="text-[#999999] text-base md:text-lg max-w-2xl mx-auto mb-8"
            >
              I'll give you exactly what I did — every product, every routine, every step. 
              But <span className="text-white">you have to actually do it.</span>
            </motion.p>
            
            <motion.div 
              variants={fadeInUp}
              className="bg-[#0a0a0a] border border-[#1a1a1a] p-6 md:p-8 max-w-xl mx-auto mb-8"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-[#666]">You get:</span>
                <span className="text-white">The exact system</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#666]">You bring:</span>
                <span className="text-[#D4AF37]">The discipline</span>
              </div>
            </motion.div>
            
            <motion.div variants={fadeInUp}>
              <div className="max-w-xs mx-auto">
                <ShopifyBuyButton productId="signal-full-guide" variant="primary" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============== SOCIAL PROOF ============== */}
      <section className="py-16 md:py-24 bg-black border-t border-[#1a1a1a]" data-testid="proof-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="font-serif text-2xl md:text-3xl text-white text-center mb-4">
              Real results from real guys
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-[#666] text-center text-sm mb-10">
              500+ men already using the system
            </motion.p>
            
            <motion.div variants={staggerContainer} className="grid md:grid-cols-3 gap-4">
              {[
                { quote: "Followed it for 3 weeks now. Skin cleared up, got my routine down. Actually seeing changes.", name: "Daan, 22" },
                { quote: "No bs, just tells you what to do. Exactly what I needed.", name: "Matteo, 26" },
                { quote: "Bought it skeptical but the structure actually helped. People started noticing.", name: "Kai, 24" },
              ].map((testimonial, idx) => (
                <motion.div 
                  key={idx}
                  variants={fadeInUp}
                  className="bg-[#0a0a0a] border border-[#1a1a1a] p-5"
                >
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-[#D4AF37] text-[#D4AF37]" />
                    ))}
                  </div>
                  <p className="text-[#999999] text-sm mb-3 italic">"{testimonial.quote}"</p>
                  <p className="text-white text-xs">— {testimonial.name}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============== TRUST / GUARANTEE ============== */}
      <section className="py-16 md:py-20 bg-[#0a0a0a] border-t border-[#1a1a1a]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center"
          >
            <motion.h3 variants={fadeInUp} className="font-serif text-xl md:text-2xl text-white mb-8">
              What you're getting
            </motion.h3>
            
            <motion.div variants={staggerContainer} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {[
                { label: "58 pages", desc: "In-depth guide" },
                { label: "Lifetime", desc: "Access forever" },
                { label: "Updates", desc: "Free included" },
                { label: "Instant", desc: "Download now" },
              ].map((item, idx) => (
                <motion.div 
                  key={idx}
                  variants={fadeInUp}
                  className="bg-black border border-[#1a1a1a] p-4"
                >
                  <p className="text-[#D4AF37] font-medium">{item.label}</p>
                  <p className="text-[#666] text-xs">{item.desc}</p>
                </motion.div>
              ))}
            </motion.div>
            
            <motion.div variants={fadeInUp} className="bg-black border border-[#1a1a1a] p-6 max-w-md mx-auto">
              <p className="text-white text-sm mb-2">Secure checkout via Shopify</p>
              <p className="text-[#666] text-xs">Your payment info is protected by industry-standard encryption</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============== FAQ PREVIEW ============== */}
      <section className="py-20 md:py-24 bg-[#0a0a0a] border-t border-[#1a1a1a]" data-testid="faq-preview-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            <motion.h3 
              variants={fadeInUp} 
              className="font-serif text-2xl md:text-3xl text-white mb-10 text-center"
            >
              Common questions
            </motion.h3>
            
            <motion.div variants={staggerContainer} className="space-y-4">
              {[
                { q: "What exactly do I get?", a: "A complete guide covering skin, hair, style, and body composition — plus the exact products and routines I personally use." },
                { q: "Is this a subscription?", a: "No. €19 one-time payment for lifetime access, including all future updates." },
                { q: "How is this delivered?", a: "Instant access through the website after purchase. View on any device, anytime." },
              ].map((faq, idx) => (
                <motion.div 
                  key={idx}
                  variants={fadeInUp}
                  whileHover={{ borderColor: '#333', transition: { duration: 0.2 } }}
                  className="bg-[#0f0f0f] border border-[#1a1a1a] p-6 transition-colors"
                >
                  <p className="text-white font-medium mb-2">{faq.q}</p>
                  <p className="text-[#999999] text-sm leading-relaxed">{faq.a}</p>
                </motion.div>
              ))}
            </motion.div>
            
            <motion.div variants={fadeInUp} className="mt-10 text-center">
              <Link 
                to="/faq" 
                className="inline-flex items-center gap-2 text-[#D4AF37] text-sm hover:gap-3 transition-all duration-300"
              >
                View all FAQs
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="py-20 md:py-28 bg-black border-t border-[#1a1a1a]" data-testid="final-cta-section">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp} 
              className="font-serif text-2xl md:text-4xl text-white mb-4"
            >
              Your glow up starts now.
            </motion.h2>
            
            <motion.p 
              variants={fadeInUp} 
              className="text-[#999999] mb-8"
            >
              Get the system. Follow it. Watch it work.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="max-w-xs mx-auto mb-4">
              <ShopifyBuyButton productId="signal-full-guide" variant="primary" />
            </motion.div>
            
            <motion.p variants={fadeInUp} className="text-sm text-[#666] mb-2">
              €19 — <span className="text-[#D4AF37]">Instant access</span>
            </motion.p>
            <motion.p variants={fadeInUp} className="text-[10px] text-[#555] max-w-xs mx-auto leading-relaxed">
              By purchasing you agree to our{' '}
              <Link to="/terms" className="text-[#D4AF37] hover:underline">terms</Link>
              {' '}and waive your right of withdrawal.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Sticky Mobile CTA - only shows after scrolling past hero */}
      <AnimatePresence>
        {showStickyCTA && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-black/95 backdrop-blur-lg border-t border-[#1a1a1a] px-4 py-3"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">The Signal: Upgrade</p>
                <div className="flex items-center gap-2">
                  <span className="text-[#D4AF37] text-sm font-semibold">€19</span>
                </div>
                <p className="text-[#666] text-xs">one-time payment</p>
              </div>
              <Link to="/products/signal-full-guide" className="flex-shrink-0">
                <Button 
                  className="bg-[#D4AF37] hover:bg-[#C9A030] text-black font-semibold px-6 py-2 text-sm rounded-none"
                >
                  Get Access
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
